import { observer } from 'mobx-react-lite'
import { Card } from '@/components/Card'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { JobProgressionCardContent } from '@/features/jobs/details/components/JobProgressionCard'
import {
  AdminProgressionActionsContainer,
  ClientProgressionActionsContainer,
  ProviderProgressionActionsContainer,
  SupportingClientProgressionActionsContainer,
} from '@/features/jobs/details/components/JobProgressionCard/ProgressionActionsContainers'

export interface JobProgressionCardContainerProps {
  vm: JobDetailsViewModel
}

export const JobProgressionCardContainer = observer(
  function JobProgressionCardContainer({
    vm,
  }: JobProgressionCardContainerProps) {
    return (
      <Card>
        <JobProgressionCardContentContainer vm={vm} />
      </Card>
    )
  },
)

export interface JobProgressionCardContentContainerProps {
  vm: JobDetailsViewModel
}

export const JobProgressionCardContentContainer = observer(
  function JobProgressionCardContentContainer({
    vm,
  }: JobProgressionCardContentContainerProps) {
    const jobProgressionVm = vm.jobProgressionVm

    const renderContent = () => (
      <JobProgressionCardContent
        jobIsCompleted={vm.job.completed}
        title={jobProgressionVm.currentStepInfo?.title}
        proDescription={jobProgressionVm.currentStepInfo?.proDescription}
        clientDescription={jobProgressionVm.currentStepInfo?.clientDescription}
        hasProgressionSteps={jobProgressionVm.hasProgressionHistory}
        completionStatus={jobProgressionVm.completionStatus}
        pendingCompletionStatus={jobProgressionVm.pendingCompletionStatus}
        isProviderCollaborator={vm.isProviderCollaborator}
        isChampionClientCollaborator={vm.isChampionClientCollaborator}
        canSetReadyToFile={vm.canSetReadyToFile}
        canApproveDraftAsSupportingClient={vm.canApproveDraftAsSupportingClient}
        onViewHistory={jobProgressionVm.historyDialogVm.modal.show}
        providerActions={
          <ProviderProgressionActionsContainer
            vm={jobProgressionVm}
            jobVm={vm}
            canCompleteJob={vm.canCompleteJobProgression}
          />
        }
        clientActions={<ClientProgressionActionsContainer vm={vm} />}
        adminActions={<AdminProgressionActionsContainer vm={vm} />}
        supportingClientActions={
          <SupportingClientProgressionActionsContainer vm={vm} />
        }
      />
    )

    return jobProgressionVm.fetch.match({
      pending: () =>
        jobProgressionVm.isRevalidating ? (
          renderContent()
        ) : (
          <ContentLoading size="section" />
        ),
      rejected: (err) => (
        <ContentError
          size="section"
          title="An error occurred while loading the job progression."
          error={err}
        />
      ),
      resolved: renderContent,
    })
  },
)
